import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "./firebase";
import { nextComparisonIndex, narrowRange, writeRankingBatch } from "./rankingUtils";
import useCosignMotion from "./fanProfile/useCosignMotion";

function ArtistCard({ artist, photoURL, onClick, disabled }) {
  return (
    <button
      type="button"
      className="artist-ranking-card"
      onClick={onClick}
      disabled={disabled}
    >
      <div className="artist-ranking-card-avatar">
        {photoURL ? <img src={photoURL} alt="" /> : (artist.artistName?.[0] || "?").toUpperCase()}
      </div>
      <strong>{artist.artistName}</strong>
      <span>{[artist.homeTown, artist.homeState].filter(Boolean).join(", ") || "Location unknown"}</span>
    </button>
  );
}

// Beli-style comparative ranking: each artist in `pendingArtists` is placed
// into the existing best-first `existingRankings` list via binary-search
// insertion, so ranking N new artists against M already-ranked ones takes
// ~N*log2(M) comparisons instead of a full manual reorder.
function ArtistRankingModal({ uid, pendingArtists, existingRankings, onClose, onComplete }) {
  const [workingList, setWorkingList] = useState(() =>
    existingRankings.map(({ artistKey, artistName, homeTown, homeState, rankedAt }) => ({
      artistKey,
      artistName,
      homeTown,
      homeState,
      rankedAt,
    }))
  );
  const [queueIndex, setQueueIndex] = useState(0);
  const [lo, setLo] = useState(0);
  const [hi, setHi] = useState(existingRankings.length);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Everyone who can appear on a card; the photos come with the co-sign data.
  const allArtists = useMemo(() => [...pendingArtists, ...existingRankings], [pendingArtists, existingRankings]);
  const boardByKey = useCosignMotion(allArtists);

  const candidate = pendingArtists[queueIndex];
  const done = queueIndex >= pendingArtists.length;
  const mid = done ? null : nextComparisonIndex(lo, hi);

  // A plain ref, not `saving` state: React 18 StrictMode double-invokes
  // effects synchronously (mount -> cleanup -> mount again) before the first
  // invocation's setSaving(true) has been committed to a render, so a
  // state-based guard doesn't stop the second invocation from also
  // auto-inserting the same candidate. A ref is mutated synchronously and is
  // shared across both invocations, so it actually prevents the double-fire.
  const insertingRef = useRef(false);

  const commitInsertion = async (insertIndex, listBeforeInsertion) => {
    insertingRef.current = true;
    const newList = [...listBeforeInsertion];
    newList.splice(insertIndex, 0, candidate);
    setWorkingList(newList);
    setSaving(true);
    setError("");
    try {
      await writeRankingBatch(db, uid, newList);
      if (queueIndex + 1 >= pendingArtists.length) {
        onComplete(newList);
        return;
      }
      setQueueIndex((i) => i + 1);
      setLo(0);
      setHi(newList.length);
    } catch (err) {
      console.error("Error saving ranking:", err);
      setError("Failed to save your ranking. Please try again.");
    } finally {
      setSaving(false);
      insertingRef.current = false;
    }
  };

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape" && !saving) onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, saving]);

  // A candidate with nothing yet to compare against (first artist ever, or
  // the working list is still empty) goes straight in at position 0.
  useEffect(() => {
    if (!done && workingList.length === 0 && !insertingRef.current) {
      commitInsertion(0, workingList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueIndex, workingList.length]);

  const handleChoice = (candidateWon) => {
    if (mid === null || saving) return;
    const narrowed = narrowRange(lo, hi, mid, candidateWon);
    if (narrowed.lo >= narrowed.hi) {
      commitInsertion(narrowed.lo, workingList);
    } else {
      setLo(narrowed.lo);
      setHi(narrowed.hi);
    }
  };

  if (done) return null;

  return (
    <div className="artist-ranking-overlay" onClick={saving ? undefined : onClose}>
      <div className="artist-ranking-modal" onClick={(e) => e.stopPropagation()}>
        <button className="artist-ranking-close" onClick={onClose} aria-label="Close" disabled={saving}>
          ×
        </button>
        <h2>Rank Your Artists</h2>
        <p className="artist-ranking-subtitle">
          Ranking {queueIndex + 1} of {pendingArtists.length}
          {workingList.length > 0 ? ` · Which artist do you like more?` : ""}
        </p>

        {error && <p className="genre-vote-error">{error}</p>}

        {workingList.length === 0 || mid === null ? (
          <p className="artist-ranking-saving">Saving...</p>
        ) : (
          <div className="artist-ranking-versus">
            <ArtistCard artist={candidate} photoURL={boardByKey[candidate.artistKey]?.photoURL}
              onClick={() => handleChoice(true)} disabled={saving} />
            <span className="artist-ranking-versus-label">vs</span>
            <ArtistCard artist={workingList[mid]} photoURL={boardByKey[workingList[mid].artistKey]?.photoURL}
              onClick={() => handleChoice(false)} disabled={saving} />
          </div>
        )}
      </div>
    </div>
  );
}

export default ArtistRankingModal;
